/**
 * Template library (K4): ready-made block layouts, grouped by purpose.
 *
 * Every template is neutral on purpose: placeholder text only, no company,
 * brand or product content. The look comes from the tenant's own brand kit
 * at send time, and the footer starts with the same sender-details and
 * unsubscribe merge fields as any new template. "Use this template" only
 * creates a draft copy (a normal block template); nothing is sent or
 * scheduled from here.
 */

import { createBlock, type Block, type FooterDefaults } from './blocks';

export type LibraryGroup = 'newsletter' | 'launch' | 'sale' | 'flow';

export const LIBRARY_GROUPS: ReadonlyArray<{ id: LibraryGroup; label: string; hint: string }> = [
  { id: 'newsletter', label: 'Newsletter', hint: 'Regular updates to your list.' },
  { id: 'launch', label: 'Product launch', hint: 'Announce something new.' },
  { id: 'sale', label: 'Sale', hint: 'Offers with a deadline or a code.' },
  { id: 'flow', label: 'Flow mails', hint: 'For automated flows such as welcome or win-back.' },
];

export interface LibraryTemplate {
  id: string;
  group: LibraryGroup;
  name: string;
  description: string;
  /** Fresh blocks (new ids) every call, ending in the required footer. */
  build: (footerDefaults?: FooterDefaults) => Block[];
}

function text(value: string): Block {
  return { ...createBlock('text'), text: value } as Block;
}

function button(label: string): Block {
  return { ...createBlock('button'), label } as Block;
}

function image(alt: string): Block {
  return { ...createBlock('image'), alt } as Block;
}

function footer(defaults: FooterDefaults | undefined): Block {
  return createBlock('footer', defaults);
}

const HELLO = 'Hi {{ contact.first_name | default: "there" }},';

export const TEMPLATE_LIBRARY: ReadonlyArray<LibraryTemplate> = [
  {
    id: 'newsletter-simple',
    group: 'newsletter',
    name: 'Simple newsletter',
    description: 'A greeting, one story with an image, and a button.',
    build: (f) => [
      text(HELLO),
      image('Describe the image'),
      text('Write the main story here. Keep it short and give the reader one clear next step.'),
      button('Read more'),
      footer(f),
    ],
  },
  {
    id: 'newsletter-roundup',
    group: 'newsletter',
    name: 'Roundup of three stories',
    description: 'An intro followed by three short items separated by dividers.',
    build: (f) => [
      text(HELLO),
      text('Here is what is new this time.'),
      { ...createBlock('divider') },
      text('Story one. Write a short summary of the first item.'),
      button('Read story one'),
      { ...createBlock('divider') },
      text('Story two. Write a short summary of the second item.'),
      button('Read story two'),
      { ...createBlock('divider') },
      text('Story three. Write a short summary of the third item.'),
      button('Read story three'),
      footer(f),
    ],
  },
  {
    id: 'launch-announcement',
    group: 'launch',
    name: 'Product announcement',
    description: 'A large image, a short pitch, one featured product and a button.',
    build: (f) => [
      image('Show the new product'),
      text('Introducing something new. Say in one or two sentences what it is and who it is for.'),
      { ...createBlock('product'), name: 'New product name' } as Block,
      button('See what is new'),
      footer(f),
    ],
  },
  {
    id: 'launch-with-proof',
    group: 'launch',
    name: 'Launch with a customer quote',
    description: 'The announcement plus a review to build trust.',
    build: (f) => [
      text(HELLO),
      image('Show the new product'),
      text('It is here. Explain the main benefit in a sentence or two.'),
      {
        ...createBlock('reviews'),
        quote: 'Add a real customer quote here.',
        source: 'Customer name',
      } as Block,
      button('Take a look'),
      footer(f),
    ],
  },
  {
    id: 'sale-deadline',
    group: 'sale',
    name: 'Sale with a deadline',
    description: 'A headline offer, the end date and a button.',
    build: (f) => [
      text('Our sale is on. Say what is reduced and by how much.'),
      { ...createBlock('countdown') },
      image('Show the offer'),
      button('Shop the sale'),
      footer(f),
    ],
  },
  {
    id: 'sale-code',
    group: 'sale',
    name: 'Discount code',
    description: 'A personal single-use code with a short note and a button.',
    build: (f) => [
      text(HELLO),
      text('Here is a little something for you. Use the code below at checkout.'),
      { ...createBlock('coupon') },
      button('Use my code'),
      footer(f),
    ],
  },
  {
    id: 'flow-welcome',
    group: 'flow',
    name: 'Welcome mail',
    description: 'A warm first mail for new subscribers.',
    build: (f) => [
      text(HELLO),
      text('Welcome, and thank you for signing up. Here is what you can expect from us.'),
      button('Get started'),
      footer(f),
    ],
  },
  {
    id: 'flow-thank-you',
    group: 'flow',
    name: 'Thank-you after a purchase',
    description: 'A short thank-you with a follow-up link.',
    build: (f) => [
      text(HELLO),
      text(
        'Thank you for your order. We hope you enjoy it. If you need help, just reply to this mail.',
      ),
      button('Need help?'),
      footer(f),
    ],
  },
  {
    id: 'flow-win-back',
    group: 'flow',
    name: 'Win-back',
    description: 'A friendly nudge with a code for people who have gone quiet.',
    build: (f) => [
      text(HELLO),
      text('It has been a while. We would love to see you again, so here is a small thank-you.'),
      { ...createBlock('coupon') },
      button('Come back'),
      footer(f),
    ],
  },
];

export function libraryByGroup(group: LibraryGroup): LibraryTemplate[] {
  return TEMPLATE_LIBRARY.filter((t) => t.group === group);
}
